import { UntypedFormGroup } from '@angular/forms'
import { Field } from '../../../abacus/interfaces/field.interface'
import { CheckboxItem, QuestionnaireField, QuestionnaireSection, Submission } from '../models/questionnaire.model'

function getVisibleRequiredQuestions(
  section: QuestionnaireSection,
  resolvedFields: Field[]
) {
  return section.questions.filter((q) => {
    if (!q.required) return false
    if (q.showWhen) {
      const field = resolvedFields.find(f => f.property === q.questionKey)
      if (field?.hidden) return false
    }
    return true
  })
}

export function calculateSectionProgressFromAnswers(
  section: QuestionnaireSection,
  resolvedFields: Field[],
  answeredQuestionIds: Set<number>
): number {
  const required = getVisibleRequiredQuestions(section, resolvedFields)
  if (required.length === 0) return 100
  const answered = required.filter((q) => answeredQuestionIds.has(q.id))
  return Math.round((answered.length / required.length) * 100)
}

export function calculateSectionProgressFromForm(
  section: QuestionnaireSection,
  resolvedFields: Field[],
  form: UntypedFormGroup
): number {
  const required = getVisibleRequiredQuestions(section, resolvedFields)
  if (required.length === 0) return 100
  const answered = required.filter((q) => {
    const value = form.get(q.questionKey)?.value
    if (value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) return false

    if (q.type === 'checkbox' && Array.isArray(value)) {
      const field = resolvedFields.find(f => f.property === q.questionKey) as QuestionnaireField | undefined
      const items = (field?.checkboxItems || q.items as CheckboxItem[] || [])
      for (const item of items) {
        if (item.hasSpecify && value.includes(item.value) && !field?.specifyText?.[item.value]?.trim()) {
          return false
        }
      }
    }

    return true
  })
  return Math.round((answered.length / required.length) * 100)
}

export function calculateAllSectionProgress(
  questionnaire: QuestionnaireSection[],
  sectionResolvedFields: Map<number, Field[]>,
  submission: Submission | null
): Map<number, number> {
  const progress = new Map<number, number>()
  const answeredQuestionIds = new Set<number>()
  if (submission?.answers?.length) {
    for (const a of submission.answers) {
      if (a.answer && a.answer.trim() !== '') {
        answeredQuestionIds.add(a.questionId)
      }
    }
  }

  for (const section of questionnaire) {
    const resolvedFields = sectionResolvedFields.get(section.sectionNumber) || []
    progress.set(
      section.sectionNumber,
      calculateSectionProgressFromAnswers(section, resolvedFields, answeredQuestionIds)
    )
  }
  return progress
}

export function calculateCompletionPercentage(
  questionnaire: QuestionnaireSection[],
  sectionResolvedFields: Map<number, Field[]>,
  sectionProgress: Map<number, number>,
  fallback: number
): number {
  if (!questionnaire.length || !sectionProgress.size) return fallback

  let totalRequired = 0
  let totalAnswered = 0
  for (const section of questionnaire) {
    const resolvedFields = sectionResolvedFields.get(section.sectionNumber) || []
    const visibleRequired = getVisibleRequiredQuestions(section, resolvedFields)
    const progress = sectionProgress.get(section.sectionNumber) ?? 0
    totalRequired += visibleRequired.length
    totalAnswered += Math.round((visibleRequired.length * progress) / 100)
  }

  return totalRequired === 0 ? 100 : Math.round((totalAnswered / totalRequired) * 100)
}
